import { ReactNode } from 'react'
import { match } from 'ts-pattern'
import {
  Text,
  IconArrowRight,
  BorderRadius8,
  GreyGrey10,
  GreyGrey30,
  GreyGrey40,
  GreyGrey60,
  GreyGrey70,
  GreyGrey80,
  GreyWhite,
  IndigoIndigo50,
  Primary12SemiBold,
  ShadowXs,
  Center,
  Box,
  Flex,
  Grid
} from '@sequencehq/core-components'

export type ComparisonVariant = 'before' | 'after'

export type ComparisonRow = {
  key: string
  before: ReactNode
  after: ReactNode
  /**
   * When true, the whole row (both cards) is hidden (opacity 0, layout kept), so
   * only the row currently being edited shows. Used on the contacts step, where
   * several rows exist at once.
   */
  dimmed?: boolean
  /**
   * When set, the row has no "before" counterpart (e.g. an added contact), so it
   * renders as a single centred card with just this one label beneath it instead
   * of a before/after pair.
   */
  singleLabel?: string
  /** Applied to the row container, so callers can address a whole row. */
  'data-testid'?: string
  /**
   * Vertical offset (e.g. `'8px'`, `'-8px'`) for the row's resting position,
   * paired with the fade so hidden rows sit just off-centre and the focused row
   * slides to centre. Callers set the sign by direction: a row below the focused
   * one rests below (positive), one above rests above (negative). Defaults to
   * centred.
   */
  offsetY?: string
}

// The grid gutter (arrow + its two column gaps) is 48px, so one `1fr` column is
// `50% - 24px`. A single (added) row reuses that width, centred, so its card
// lines up with the "after" cards of the paired rows above/below it.
const SINGLE_CARD_WIDTH = 'calc(50% - 24px)'

/**
 * Shared before/after layout for the review modal preview. Each row is a
 * self-contained block: a `1fr auto 1fr` grid (before card | arrow | after card)
 * with a BEFORE/AFTER label pair beneath it. A row flagged `singleLabel` has no
 * "before" side, so it renders as a single centred card with that one label
 * instead. The rows are stacked in one centred grid cell (see below): with the
 * contacts step showing one row at a time, the focused row cross-fades in place.
 * Used by both the customer-info and billing-contact comparisons.
 */
export const BeforeAfterComparison = ({
  rows,
  'data-testid': dataTestId
}: {
  rows: ComparisonRow[]
  'data-testid'?: string
}) => (
  // All rows share one grid cell, stacked and centred, so the visible (focused)
  // row stays put as the others cross-fade under it at opacity 0.
  <Box display="grid" alignItems="center" data-testid={dataTestId}>
    {rows.map(row => (
      <Box key={row.key} gridColumn={1} gridRow={1} minWidth="0">
        <ComparisonRowView row={row} />
      </Box>
    ))}
  </Box>
)

const ComparisonRowView = ({ row }: { row: ComparisonRow }) => (
  <Flex
    flexDirection="column"
    gap="8px"
    opacity={row.dimmed ? 0 : 1}
    transform={`translateY(${row.offsetY ?? '0px'})`}
    transition="opacity 0.12s linear, transform 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94)"
    // Hidden rows sit under the visible one, so stop them intercepting hovers
    // (e.g. the cards' overflow tooltips) and clicks.
    pointerEvents={row.dimmed ? 'none' : undefined}
    data-testid={row['data-testid']}
  >
    {row.singleLabel !== undefined ? (
      <>
        <Flex justifyContent="center">
          <Box width={SINGLE_CARD_WIDTH} minWidth="0">
            {row.after}
          </Box>
        </Flex>
        <Flex justifyContent="center">
          <Box width={SINGLE_CARD_WIDTH}>
            <CardLabel>{row.singleLabel}</CardLabel>
          </Box>
        </Flex>
      </>
    ) : (
      <>
        <Grid gridTemplateColumns="1fr auto 1fr" columnGap="16px">
          <Box gridColumn={1} minWidth="0">
            {row.before}
          </Box>
          <Center gridColumn={2} color={GreyGrey60}>
            <IconArrowRight />
          </Center>
          <Box gridColumn={3} minWidth="0">
            {row.after}
          </Box>
        </Grid>
        <Grid gridTemplateColumns="1fr auto 1fr" columnGap="16px">
          <CardLabel>Before</CardLabel>
          {/* Spacer matching the arrow column so the labels sit under their cards. */}
          <Box width="16px" />
          <CardLabel>After</CardLabel>
        </Grid>
      </>
    )}
  </Flex>
)

/**
 * Box styling for a comparison card: muted "before", emphasised "after". When
 * `highlighted`, the card's form is the one currently being edited, so its border
 * turns indigo to track the operator's focus in the preview.
 */
export const getComparisonCardStyle = (
  variant: ComparisonVariant,
  highlighted = false
) => {
  const style = match(variant)
    .with('before', () => ({
      background: GreyGrey10,
      borderColor: GreyGrey30,
      color: GreyGrey70,
      boxShadow: 'none'
    }))
    .with('after', () => ({
      background: GreyWhite,
      borderColor: GreyGrey40,
      color: GreyGrey80,
      boxShadow: ShadowXs
    }))
    .exhaustive()

  return highlighted ? { ...style, borderColor: IndigoIndigo50 } : style
}

/**
 * The bordered-frame box props shared by every comparison card (customer card,
 * billing-contact card, empty placeholder), so the frame chrome has a single
 * source of truth and can't drift between them. Padding/overflow/width are left
 * to each caller, as those differ by card body.
 */
export const getComparisonCardFrame = (
  variant: ComparisonVariant,
  highlighted = false
) => {
  const { borderColor, background, boxShadow } = getComparisonCardStyle(
    variant,
    highlighted
  )

  return {
    border: '1px solid',
    borderColor,
    background,
    boxShadow,
    borderRadius: BorderRadius8
  }
}

/**
 * The bordered card frame shared by the customer and billing-contact cards: the
 * variant box styling, an optional leading logo, and a content column the caller
 * fills (e.g. name + address, or name + email + badges).
 */
export const ComparisonCard = ({
  variant,
  logo,
  children,
  highlighted = false,
  'data-testid': dataTestId
}: {
  variant: ComparisonVariant
  logo?: ReactNode
  children: ReactNode
  /** Rings the card in indigo when its form is the one being edited. */
  highlighted?: boolean
  'data-testid'?: string
}) => (
  <Box
    {...getComparisonCardFrame(variant, highlighted)}
    paddingX="16px"
    paddingY="12px"
    // Fills the comparison row so the before and after cards stay the same
    // height when one holds less than the other (e.g. a customer gaining an
    // address) — the extra space goes to the card, not to its padding.
    height="100%"
    data-testid={dataTestId}
  >
    <Flex gap="16px" alignItems="flex-start">
      {logo}
      <Flex flexDirection="column" gap="4px" flex="1" minWidth="0">
        {children}
      </Flex>
    </Flex>
  </Box>
)

const CardLabel = ({ children }: { children: string }) => (
  <Text
    flex="1"
    textAlign="center"
    textTransform="uppercase"
    {...Primary12SemiBold}
    color={GreyGrey60}
  >
    {children}
  </Text>
)
